// Montclair State University: public college page (#2839), official facts only.
//
// Every figure below was read on 2026-10-01 from the source it names:
//  - Montclair's Common Data Set 2025-2026 (Institutional Research and
//    Effectiveness; the PDF sits on irdata.montclair.edu and the source link
//    is the inside.montclair.edu page that lists every edition, 2025-2026 the
//    newest; Bloomfield College has its own CDS on the same page): C1
//    applicants, admits and enrollees for Fall 2025 with the residency table
//    (Unknown is 0 and left out), C8 test policy for Fall 2027, C21/C22
//    plans. C21: no early decision plan. C22: a nonbinding, not restrictive
//    early action plan (closing 12/1, notification 12/23), with no counts.
//  - Montclair Undergraduate Admissions, How to Apply to Montclair's
//    Undergraduate Programs: the Fall 2027 deadlines table, decision dates,
//    test policy, Nursing and College of the Arts requirements. Departure:
//    the CDS gives December 23 as the early action notification date; the
//    admissions page says December 24, 2026, and the admissions page is used.
//  - Montclair Tuition and Fees, Undergraduate Student Costs: the 2026-2027
//    on-campus cost of attendance, item by item. Departure: the CDS G1 box
//    is checked "not available at this time" for 2026-2027 costs, so the
//    aid office's 2026-2027 estimates are used. Its items sum to its totals.
//  - College Scorecard API, school id 185590 (net price by family income,
//    in-state).
// Nothing here is profile-derived, an odds figure or a student record.
// Re-check a figure against its source before editing it, and move
// `lastChecked` when you do.

export const SOURCES = {
    cds: {
        label: 'Montclair State University Common Data Set 2025-2026',
        url: 'https://inside.montclair.edu/administration/institutional-research-and-effectiveness-office/institutional-reports/common-data-set',
        read: '2026-10-01',
    },
    apply: {
        label: 'Montclair Undergraduate Admissions, How to Apply to Montclair\'s Undergraduate Programs',
        url: 'https://www.montclair.edu/admissions-aid/how-apply-montclair-state-university/how-apply-montclairs-undergraduate-programs',
        read: '2026-10-01',
    },
    coa: {
        label: 'Montclair Tuition and Fees, Undergraduate Student Costs',
        url: 'https://www.montclair.edu/admissions-aid/tuition-and-fees/undergraduate-student-costs',
        read: '2026-10-01',
    },
    scorecard: {
        label: 'College Scorecard, U.S. Department of Education',
        url: 'https://collegescorecard.ed.gov/school/?185590-Montclair-State-University',
        read: '2026-10-15',
    },
};

const college = {
    slug: 'montclair-state-university',
    kbId: 'montclair_state_university',
    name: 'Montclair State University',
    shortName: 'Montclair State',
    aliases: ['Montclair'],
    location: 'Montclair, NJ',
    control: 'Public',
    lastChecked: '2026-10-01',
    sources: SOURCES,
    // From the KB's sourced branding (Montclair brand guidelines); decoration only.
    branding: {
        primary: '#D1190D',
        secondary: '#333333',
        logo_url: 'https://storage.googleapis.com/college-counselor-assets/university-logos/montclair_state_university_logo.png',
    },

    admission: {
        entering: 'Fall 2025',
        cdsCycle: '2025-2026',
        applied: 26224,
        admitted: 23443,
        enrolled: 3816,
        source: 'cds',
        where: 'section C1',
        residency: [
            { group: 'In-state', applied: 21206, admitted: 19137 },
            { group: 'Out-of-state', applied: 3559, admitted: 3086 },
            { group: 'International', applied: 1459, admitted: 1220 },
        ],
        residencySource: 'cds',
        residencyWhere: 'section C1, residency table',
    },
    // Acceptance by school or major: shown only where the school publishes it.
    byMajor: {
        published: false,
        note: 'Montclair\'s Common Data Set reports one first-year acceptance rate for the university, with no rate by college or major. Montclair posts a separate Common Data Set for Bloomfield College of Montclair State University.',
        source: 'cds',
    },
    earlyRounds: {
        title: 'Early action',
        note: 'Montclair has no early decision plan. Its Early Action plan is non-binding and does not limit where else you apply early. The Common Data Set gives no early action counts, so it gives no early round acceptance rate.',
        source: 'cds',
        where: 'sections C21 and C22',
    },

    programs: {
        entering: 'Fall 2027',
        intro: 'You apply with the Montclair application or the Common App, plus a transcript and an essay. Some programs ask for more:',
        rows: [
            { school: 'School of Nursing', requirement: 'A nursing essay and extra recommendation letters. The Nursing deadline is February 1, 2027, and Nursing applicants hear by April 1, 2027.' },
            { school: 'College of the Arts', requirement: 'Animation and Visual Effects, Dance, Illustration, Music, Musical Theatre, Recording Arts, Theatre and Visual Communication Design need an interview, audition or portfolio review after you apply. Montclair emails the instructions once your application is in. Admission to Montclair does not guarantee a place in the program.' },
            { school: 'Dance, Musical Theatre and Theatre', requirement: 'Two recommendation letters, at least one about your readiness for the field, and a resume. An audition fee applies to Dance, Music, Music Therapy, Musical Theatre and Theatre.' },
        ],
        source: 'apply',
    },

    deadlines: {
        entering: 'Fall 2027',
        headline: { label: 'Regular admission', value: 'March 1' },
        rows: [
            { plan: 'Early Action', deadline: 'December 1, 2026', decision: 'By December 24, 2026', binding: false },
            { plan: 'School of Nursing', deadline: 'February 1, 2027', decision: 'By April 1, 2027', binding: false },
            { plan: 'Regular admission and scholarship consideration', deadline: 'March 1, 2027', decision: '3 to 6 weeks after all materials arrive', binding: false },
        ],
        note: 'Montclair reviews applications on a rolling basis and begins sending decisions in October. College of the Arts applicants may hear after December 24 because of their audition or portfolio review. To be considered for scholarships, complete your application by March 1, 2027. The application fee is $65, and a fee waiver is available based on financial need.',
        source: 'apply',
    },

    testing: {
        entering: 'Fall 2027',
        policy: 'Test-optional',
        blind: false,
        line: 'Sending SAT or ACT scores is optional at Montclair. Montclair encourages you to send scores only if you believe they strengthen your application.',
        source: 'apply',
        cdsLine: 'The Common Data Set also marks the SAT or ACT as not required but considered if submitted for fall 2027 applicants, and adds: "totally test optional."',
        cdsWhere: 'section C8',
    },

    // The next cycle (2027-28, fall 2028 entry): only what the school has
    // announced, read on the date below; nothing announced says so (#2651).
    nextCycle: {
        entering: 'Fall 2028',
        checked: '2026-10-01',
        changes: [],
        source: 'apply',
    },

    cost: {
        year: '2026-2027',
        columns: ['In-state', 'Out-of-state'],
        rows: [
            { item: 'Tuition', amounts: [16588, 27924] },
            { item: 'Fees', amounts: [1222, 1222] },
            { item: 'Loan fee', amounts: [72, 72] },
            { item: 'Housing (on campus)', amounts: [10838, 10838] },
            { item: 'Food', amounts: [6612, 6612] },
            { item: 'Books', amounts: [1330, 1330] },
            { item: 'Travel', amounts: [1380, 1380] },
            { item: 'Miscellaneous', amounts: [2430, 2430] },
        ],
        totals: [40472, 51808],
        note: 'For a full-time undergraduate in a bachelor\'s program living on campus, fall and spring. Montclair\'s figures are estimates; if you apply for aid, the Financial Aid Office sets a personal cost of attendance.',
        source: 'coa',
        netPrice: {
            average: 15193,
            byIncome: [
                { income: '$0 to $30,000', amount: 10117 },
                { income: '$30,001 to $48,000', amount: 10520 },
                { income: '$48,001 to $75,000', amount: 13862 },
                { income: '$75,001 to $110,000', amount: 21770 },
                { income: 'Over $110,000', amount: 25855 },
            ],
            note: 'What first-year, full-time, in-state students who received federal aid paid after grants and scholarships, by family income, in 2024-25, the latest year the Department of Education publishes. Your own price comes from Montclair\'s aid offer.',
            tileDetail: 'Per year, after grants, in-state federal aid recipients',
            source: 'scorecard',
        },
    },
};

export default college;
